import React, { useState, useEffect, useRef } from 'react';
import { PRIMARY } from '../utils/parameterUtils';

/**
 * AdvancedSettingsModal - Modal for editing advanced parameters
 * Displays all parameters with appropriate input types (sliders, buttons, text)
 */
export default function AdvancedSettingsModal({
  open,
  onClose,
  item,
  textInputs,
  setTextInputs,
  settings,
  setSettings,
  textParams,
  selectParams,
  imageParams,
  formatParamLabel,
}) {
  const [showAll, setShowAll] = useState(false);
  const [expandedHelp, setExpandedHelp] = useState({});

  // Get all params for display
  const allParams = item?.parameters?.filter(p => !p.is_private && Number(p.is_private) !== 1) || [];
  
  // Params already handled in main UI
  const handledParams = new Set([
    ...textParams.map(tp => tp.parameter_name),
    ...selectParams.map(sp => sp.parameter_name),
    ...imageParams.map(ip => ip.paramName),
  ]);
  
  // Filter params based on showAll toggle
  const displayParams = showAll 
    ? allParams 
    : allParams.filter(p => !handledParams.has(p.parameter_name));

  // Get/set value for a param
  const getValue = (param) => {
    return textInputs[param.parameter_name] ?? settings[param.parameter_name] ?? param.default_value ?? '';
  };

  const setValue = (param, value) => {
    setTextInputs(prev => ({ ...prev, [param.parameter_name]: value }));
  };

  // Reset to defaults
  const handleReset = () => {
    if (!item?.parameters) return;
    const defaults = {};
    item.parameters.forEach(p => {
      if (p.default_value !== undefined && p.default_value !== null) {
        defaults[p.parameter_name] = p.default_value;
      }
    });
    setTextInputs(prev => ({ ...prev, ...defaults }));
    setSettings(prev => ({ ...prev, ...defaults }));
  };

  // Render input based on param type
  const renderInput = (param) => {
    const value = getValue(param);
    const min = param.allowed_min_value ?? param.min_value ?? 0;
    const max = param.allowed_max_value ?? param.max_value ?? 10000;
    const step = param.data_type === 'float' ? 0.1 : 1;
    const showSlider = (max - min) <= 500 && (max - min) > 0;

    // Number with slider
    if (param.data_type === 'number' || param.data_type === 'integer' || param.data_type === 'float') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {showSlider && (
            <input
              type="range"
              value={value || min}
              onChange={(e) => {
                const v = param.data_type === 'float' ? parseFloat(e.target.value) : parseInt(e.target.value);
                setValue(param, v);
              }}
              min={min}
              max={max}
              step={step}
              style={{ 
                flex: 1, 
                height: 4,
                accentColor: PRIMARY,
              }}
            />
          )}
          <input
            type="number"
            value={value || ''}
            onChange={(e) => {
              const v = param.data_type === 'float' ? parseFloat(e.target.value) : parseInt(e.target.value);
              setValue(param, isNaN(v) ? '' : v);
            }}
            min={min}
            max={max}
            step={step}
            placeholder={`${min} - ${max}`}
            style={{
              width: showSlider ? 70 : '100%',
              height: 40,
              padding: '0 12px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              color: '#fff',
              fontSize: 14,
              textAlign: 'center',
            }}
          />
        </div>
      );
    }

    // Select with allowed values
    if (param.allowed_values) {
      let options = param.allowed_values;
      if (typeof options === 'string') {
        try { options = JSON.parse(options); } 
        catch { options = options.split(',').map(v => v.trim()); }
      }
      if (!Array.isArray(options)) options = [options];

      return (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {options.map(opt => {
            const isSelected = String(value) === String(opt);
            return (
              <button
                key={opt}
                onClick={() => setValue(param, opt)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  background: isSelected ? PRIMARY : 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: isSelected ? '#fff' : '#d1d5db',
                  fontSize: 13,
                  fontWeight: isSelected ? 600 : 400,
                  cursor: 'pointer',
                }}
              >
                {String(opt).replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
      );
    }

    // Object/array types - show placeholder
    if (param.data_type === 'object' || param.data_type === 'object[]') {
      return (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => setValue(param, e.target.value)}
          placeholder={`Enter ${param.parameter_name}`}
          style={{
            width: '100%',
            height: 40,
            padding: '0 12px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8,
            color: '#fff',
            fontSize: 14,
          }}
        />
      );
    }

    // Default text input
    return (
      <input
        type="text"
        value={value || ''}
        onChange={(e) => setValue(param, e.target.value)}
        placeholder={`Enter ${param.parameter_name}`}
        style={{
          width: '100%',
          height: 40,
          padding: '0 12px',
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 8,
          color: '#fff',
          fontSize: 14,
        }}
      />
    );
  };

  // Refs for focus management
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Focus management - trap focus in modal and handle Escape
  useEffect(() => {
    if (!open) return;
    
    // Focus close button when modal opens
    closeButtonRef.current?.focus();
    
    // Handle Escape key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="advanced-settings-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.8)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxHeight: '85vh',
          background: '#0f0f0f',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <h3 id="advanced-settings-title" style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#fff' }}>
            Advanced Settings
          </h3>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close advanced settings"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Show All Toggle */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(255,255,255,0.02)',
        }}>
          <span style={{ fontSize: 13, color: '#9ca3af' }}>
            Show all parameters
            <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 6 }}>
              ({allParams.length} total)
            </span>
          </span>
          <button
            onClick={() => setShowAll(!showAll)}
            style={{
              width: 44,
              height: 24,
              borderRadius: 12,
              border: 'none',
              background: showAll ? PRIMARY : 'rgba(255,255,255,0.1)',
              cursor: 'pointer',
              position: 'relative',
              transition: 'background 0.2s',
            }}
          >
            <span style={{
              position: 'absolute',
              top: 2,
              left: showAll ? 22 : 2,
              width: 20,
              height: 20,
              borderRadius: 10,
              background: '#fff',
              transition: 'left 0.2s',
            }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {displayParams.length > 0 ? (
            displayParams.map(param => {
              const isInMainUI = handledParams.has(param.parameter_name);
              const helpExpanded = expandedHelp[param.parameter_name];
              
              return (
                <div
                  key={param.parameter_name}
                  style={{
                    marginBottom: 16,
                    padding: '14px 16px',
                    background: 'rgba(255,255,255,0.02)',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  {/* Label Row */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8, 
                    marginBottom: param.description && helpExpanded ? 6 : 12 
                  }}>
                    <label style={{ fontSize: 14, fontWeight: 500, color: '#f5f5f5' }}>
                      {formatParamLabel(param.parameter_name, param.parameter_title)}
                    </label>
                    {param.description && (
                      <button
                        onClick={() => setExpandedHelp(prev => ({ 
                          ...prev, 
                          [param.parameter_name]: !prev[param.parameter_name] 
                        }))}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 2,
                          cursor: 'pointer',
                          color: helpExpanded ? PRIMARY : '#6b7280',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                      </button>
                    )}
                    {isInMainUI && (
                      <span style={{
                        fontSize: 10,
                        padding: '2px 8px',
                        borderRadius: 4,
                        background: 'rgba(250, 139, 20, 0.15)',
                        color: PRIMARY,
                        fontWeight: 500,
                        marginLeft: 'auto',
                      }}>
                        In Main UI
                      </span>
                    )}
                  </div>
                  
                  {/* Description */}
                  {param.description && helpExpanded && (
                    <div style={{
                      fontSize: 12,
                      color: '#9ca3af',
                      marginBottom: 12,
                      padding: '8px 10px',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: 6,
                      lineHeight: 1.5,
                      maxHeight: 100,
                      overflow: 'auto',
                    }}>
                      {param.description}
                    </div>
                  )}
                  
                  {/* Input */}
                  {renderInput(param)}
                </div>
              );
            })
          ) : (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 16px', display: 'block' }}>
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              <p style={{ margin: 0, fontSize: 14 }}>No additional settings available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 20px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          gap: 10,
        }}>
          <button
            onClick={handleReset}
            style={{
              flex: 1,
              height: 44,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 10,
              color: '#9ca3af',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Reset
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              height: 44,
              background: PRIMARY,
              border: 'none',
              borderRadius: 10,
              color: '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
